import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getStoredUser, clearAuth, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { TrendingUp, ShieldCheck, LogOut, ArrowLeft } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ChangePassword() {
  const user = getStoredUser();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const logout = () => {
    clearAuth();
    nav("/login");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    if (next === current) return toast.error("New password must be different from current");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] grain-bg">
      <Toaster richColors position="top-right" />
      <div className="w-full max-w-md mx-4">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="relative">
            <TrendingUp className="w-9 h-9" style={{ color: "#0A4C95" }} strokeWidth={2.5} />
            <div className="absolute -right-1 -bottom-1 w-3 h-3 rounded-full" style={{ background: "#F05D23" }} />
          </div>
          <div>
            <div className="font-bold text-2xl tracking-tight text-slate-900">Stryde</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 -mt-0.5">Finance</div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg shadow-sm p-8">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-[#0A4C95]" />
            <h1 className="text-xl font-bold text-slate-900">Change password</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">Signed in as {user?.email}.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="current">Current password</Label>
              <Input id="current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoFocus autoComplete="current-password" data-testid="cp-current-input" />
            </div>
            <div>
              <Label htmlFor="next">New password (≥ 8 characters)</Label>
              <Input id="next" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="cp-new-input" />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="cp-confirm-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#0A4C95] hover:bg-[#083d79]" data-testid="cp-submit-btn">
              {loading ? "Updating..." : "Update password"}
            </Button>
            <div className="flex items-center justify-between pt-1">
              <Button type="button" variant="ghost" onClick={() => nav("/")} className="text-slate-500" data-testid="cp-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
              <Button type="button" variant="ghost" onClick={logout} className="text-slate-500 hover:text-red-600">
                <LogOut className="w-4 h-4 mr-1" /> Sign out
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
